import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Search, Check, Copy, Sparkles, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

export const LibraryPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [promptsRes, categoriesRes] = await Promise.all([
          api.get('/prompts'),
          api.get('/prompts/categories')
        ]);
        setPrompts(promptsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleCopy = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.prompt_text);
      setCopiedId(prompt.id);
      toast.success('Skopiowano!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error('Nie udało się skopiować');
    }
  };

  const filteredPrompts = prompts.filter((p) => {
    const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
    const matchesSearch =
      !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.short_description.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-20">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 140px)',
        background: 'transparent'
      }}
      data-testid="library-page"
    >
      <div className="px-6 pt-8 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-slate-400 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>

        <h1 className="text-2xl font-bold text-white mb-2">Biblioteka promptów</h1>
        <p className="text-slate-400 mb-4">Gotowe prompty do skopiowania</p>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="Szukaj promptów..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 rounded-xl text-white placeholder:text-slate-500"
            style={{
              background: 'rgba(255,255,255,0.08)',
              borderColor: 'rgba(255,255,255,0.15)'
            }}
          />
        </div>
      </div>

      <div className="px-6 mb-4 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 pb-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              activeCategory === 'all'
                ? 'bg-sky-500 text-white'
                : 'bg-white/10 text-slate-300 hover:bg-white/20'
            }`}
          >
            Wszystkie
          </button>

          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat
                  ? 'bg-sky-500 text-white'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>



      <div className="px-6 space-y-3">
        {filteredPrompts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Nie znaleziono promptów</p>
          </div>
        ) : (
          filteredPrompts.map((prompt, idx) => (
            <Card
              key={prompt.id}
              className="card-premium animate-slideUp border-0"
              style={{
                animationDelay: `${idx * 0.03}s`,
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <CardContent className="py-4">
                <span className="text-xs bg-sky-500/20 text-sky-400 px-2 py-1 rounded-full font-medium">
                  {prompt.category}
                </span>

                <h3 className="font-semibold text-white mt-2">{prompt.title}</h3>
                <p className="text-sm text-slate-400 mt-1">{prompt.short_description}</p>

                <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <p className="text-sm text-slate-300 font-mono line-clamp-3">
                    {prompt.prompt_text}
                  </p>
                </div>

                <Button
                  onClick={() => handleCopy(prompt)}
                  className={`w-full mt-3 h-10 rounded-xl font-medium btn-press ${
                    copiedId === prompt.id
                      ? 'bg-emerald-500 hover:bg-emerald-600'
                      : 'bg-sky-500 hover:bg-sky-600'
                  }`}
                >
                  {copiedId === prompt.id ? (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Skopiowano!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" /> Kopiuj
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
